import { findChallengeById } from '@devlearn/content';
import { baseXpFor, type ProgressResponse } from '@devlearn/shared';
import { ArrowRightIcon, CheckIcon } from '@/components/icons';
import { Button, Card, ProgressBar } from '@/components/ui';
import { challengePath } from '@/features/content';
import { preloadEditor } from '@/features/editor';

type ContinueCardProps = { progress: ProgressResponse; className?: string };

/** "Continue where you left off" — the emphasized card on the dashboard. */
export function ContinueCard({ progress, className }: ContinueCardProps) {
  const current = progress.currentChallenge ? findChallengeById(progress.currentChallenge.id) : undefined;

  if (!current) {
    return (
      <Card as="section" emphasized aria-labelledby="continue-h" className={className}>
        <div className="flex flex-col gap-3">
          <p className="m-0 text-meta font-semibold uppercase tracking-[0.04em] text-ink-muted">
            Track complete
          </p>
          <h2 id="continue-h" className="m-0 flex items-center gap-2 text-2xl font-semibold">
            <CheckIcon size={22} className="text-success" /> You finished JavaScript Fundamentals
          </h2>
          <p className="m-0 text-body-app text-ink-soft">
            Keep it fresh with recaps from the Practice tab. New tracks are on the way.
          </p>
          <Button to="/practice" variant="secondary" className="self-start">
            Go to practice
          </Button>
        </div>
      </Card>
    );
  }

  const state = progress.challenges.find((c) => c.id === current.challenge.id);
  const chapter = progress.chapters.find((c) => c.id === current.chapter.id);
  const xp = baseXpFor(current.challenge.type);
  const status = state?.lastAttempt
    ? `${state.lastAttempt.passedCount} of ${state.lastAttempt.totalCount} tests passing · ${xp} XP`
    : `Not started · ${xp} XP`;
  const isFirstEver = progress.totals.completed === 0 && !state?.lastAttempt;
  const label = isFirstEver ? 'Start chapter 1' : 'Continue';

  return (
    <Card as="section" emphasized aria-labelledby="continue-h" className={className}>
      <div className="flex flex-col gap-4">
        <p className="m-0 text-meta font-semibold uppercase tracking-[0.04em] text-ink-muted">
          {isFirstEver ? 'Start here' : 'Continue where you left off'}
        </p>
        <div className="flex flex-col gap-4 md:flex-row md:items-end md:justify-between md:gap-6">
          <div className="flex min-w-0 flex-col gap-1.5">
            <span className="text-body-app text-ink-muted">
              Chapter {current.chapter.position} · {current.chapter.title}
            </span>
            <h2 id="continue-h" className="m-0 text-xl font-semibold tracking-[-0.01em] md:text-[26px]">
              {current.challenge.title}
            </h2>
            <span className="font-mono text-meta text-ink-muted">{status}</span>
          </div>
          <Button
            to={challengePath(current)}
            size="lg"
            iconRight={<ArrowRightIcon />}
            className="w-full shrink-0 md:w-auto"
            onMouseEnter={preloadEditor}
            onFocus={preloadEditor}
          >
            {label}
          </Button>
        </div>
        {chapter && (
          <div className="flex items-center gap-3">
            <ProgressBar
              value={chapter.completed}
              max={chapter.total}
              label={`Chapter ${current.chapter.position} progress`}
              className="flex-1"
            />
            <span className="font-mono text-meta text-ink-muted tabular-nums">
              {chapter.completed} / {chapter.total} in chapter
            </span>
          </div>
        )}
      </div>
    </Card>
  );
}
